import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Booking } from '../types/BookingModel';

/** All bookings of one person with their sums (net < 0 = Verbindlichkeit, > 0 = Forderung). */
export interface PersonGroup {
  person: Booking['person'];
  bookings: Booking[];
  creditSum: number;
  debitSum: number;
  net: number;
}

function fmt(v: number) {
  return v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/** Groups bookings by person (bookings by date, persons by name). */
export function groupBookingsByPerson(bookings: Booking[]): PersonGroup[] {
  const grouped = bookings.reduce((acc, booking) => {
    const id = booking.person?.id ?? 'unknown';
    if (!acc[id]) acc[id] = { person: booking.person, bookings: [] };
    acc[id].bookings.push(booking);
    return acc;
  }, {} as Record<string, { person: Booking['person']; bookings: Booking[] }>);

  return Object.values(grouped)
    .map(({ person, bookings }) => {
      const sorted = bookings.slice().sort(
        (a, b) => new Date(a.bookingDate).getTime() - new Date(b.bookingDate).getTime()
      );
      const creditSum = sorted
        .filter((b) => (b.bookingType?.multiplier ?? 0) > 0)
        .reduce((s, b) => s + b.bookingAmount, 0);
      const debitSum = sorted
        .filter((b) => (b.bookingType?.multiplier ?? 0) < 0)
        .reduce((s, b) => s + b.bookingAmount, 0);
      return { person, bookings: sorted, creditSum, debitSum, net: creditSum - debitSum };
    })
    .sort((a, b) =>
      `${a.person?.lastName} ${a.person?.firstName}`.localeCompare(
        `${b.person?.lastName} ${b.person?.firstName}`
      )
    );
}

/** One page per person with all bookings, sums and the resulting Forderung/Verbindlichkeit. */
export function generateAccountReportPdf(groups: PersonGroup[], fileName?: string) {
  const doc = new jsPDF();
  const today = dayjs().format('DD.MM.YYYY');

  groups.forEach((group, index) => {
    if (index > 0) doc.addPage();

    const { person, bookings, creditSum, debitSum, net } = group;
    const name = `${person?.lastName} ${person?.firstName}`;

    doc.setFontSize(14);
    doc.text(name, 14, 16);
    doc.setFontSize(9);
    if (person?.iban) doc.text(`IBAN: ${person.iban}`, 14, 22);
    doc.text(`Stand: ${today}`, 14, person?.iban ? 27 : 22);

    const startY = person?.iban ? 33 : 28;

    autoTable(doc, {
      startY,
      head: [['Datum', 'Typ', 'Text', 'Soll', 'Haben']],
      body: [
        ...bookings.map((b) => {
          const isCredit = (b.bookingType?.multiplier ?? 0) > 0;
          return [
            dayjs(b.bookingDate).format('DD.MM.YYYY'),
            b.bookingType?.title ?? '',
            b.bookingText,
            !isCredit ? fmt(b.bookingAmount) : '',
            isCredit ? fmt(b.bookingAmount) : '',
          ];
        }),
        ['', '', 'Summe', fmt(debitSum), fmt(creditSum)],
        [
          '', '',
          net < 0 ? 'Verbindlichkeit' : net > 0 ? 'Forderung' : '',
          net < 0 ? fmt(Math.abs(net)) : '',
          net > 0 ? fmt(net) : '',
        ],
      ],
      styles: { fontSize: 8 },
      headStyles: { fillColor: [60, 60, 60] },
      columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' } },
      didParseCell: (data) => {
        const totalRows = bookings.length;
        if (data.row.index >= totalRows) {
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
  });

  doc.save(fileName ?? `verbindlichkeiten-bericht-${today}.pdf`);
}
